import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FileText, LayoutGrid } from "lucide-react";
import api from "../../../services/apiService";
import AlertNotification from "../../../components/notification";
import HrEmployeeDocuments from "./HrEmployeeDocuments";
import { usePageTitle } from "../../../components/layouts/PageTitleContext";
import UserAvatar from "../../../components/ui/UserAvatar";

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return { Authorization: token ? `Bearer ${token}` : undefined };
};

const STATUS_BADGE = {
  active:     "bg-green-50 border-green-200 text-green-700 dark:bg-green-500/15 dark:border-green-500/30 dark:text-green-300",
  probation:  "bg-yellow-50 border-yellow-200 text-yellow-700 dark:bg-yellow-500/15 dark:border-yellow-500/30 dark:text-yellow-300",
  resigned:   "bg-gray-100 border-gray-200 text-gray-500 dark:bg-slate-700 dark:border-slate-600 dark:text-slate-300",
  terminated: "bg-red-50 border-red-200 text-red-700 dark:bg-red-500/15 dark:border-red-500/30 dark:text-red-300",
};

const TABS = [
  { label: "Overview",  icon: LayoutGrid },
  { label: "Documents", icon: FileText },
];

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-app-muted">{label}</p>
      <p className="mt-0.5 break-words text-sm text-app-primary">{value || "—"}</p>
    </div>
  );
}

function HrEmployeeDetail() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [activeTab, setActiveTab] = useState("Overview");

  const fetchEmployee = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await api.get(`/hr/employees/${id}`, { headers: authHeaders() });
      setEmployee(res.data);
    } catch {
      setNotification({ text: "Failed to load employee.", status: false });
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchEmployee(); }, [fetchEmployee]);

  usePageTitle(employee?.fullName);

  if (isLoading) return <div className="flex justify-center py-16 text-app-muted">Loading…</div>;
  if (!employee) return <div className="flex justify-center py-16 text-app-muted">Employee not found.</div>;

  const e = employee;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {e.employeeCode && (
            <span className="font-mono text-xs text-app-muted">{e.employeeCode}</span>
          )}
          <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold capitalize ${STATUS_BADGE[e.status] || ""}`}>
            {e.status}
          </span>
        </div>
        <button onClick={() => navigate(`/hr/employees/${id}/edit`)}
          className="rounded-lg bg-[#2470B7] border border-[#2470B7] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#1f5f9b] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 disabled:cursor-not-allowed disabled:opacity-60 dark:border-blue-600 dark:bg-blue-600 dark:hover:bg-blue-500">
          Edit
        </button>
      </div>

      <div className="flex gap-1 border-b border-app-primary">
        {TABS.map(({ label, icon: Icon }) => (
          <button key={label} onClick={() => setActiveTab(label)}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold transition border-b-2 -mb-px ${
              activeTab === label ? "border-[#2470B7] text-[#2470B7] dark:border-blue-400 dark:text-blue-400" : "border-transparent text-app-muted hover:border-app-primary hover:text-app-primary"
            }`}>
            <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
            {label}
          </button>
        ))}
      </div>

      {activeTab === "Overview" && (
        <div className="rounded-xl border border-app-primary bg-app-card shadow-sm px-6 py-6 dark:shadow-none">
          <div className="grid grid-cols-2 gap-5 sm:grid-cols-3">
            <Field label="Designation"     value={e.designation} />
            <Field label="Department"      value={e.department} />
            <Field label="Email"           value={e.email} />
            <Field label="Phone"           value={e.phone} />
            <Field label="CNIC"            value={e.cnic} />
            <Field label="Location"        value={e.location?.name} />
            <Field label="Employment Type" value={e.employmentType?.replace("_", " ")} />
            <Field label="Work Mode"       value={e.workMode} />
            <Field label="Joining Date"    value={e.joiningDate} />
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-app-muted">Linked Account</p>
              {e.credentials ? (
                <button type="button" onClick={() => navigate(`/profile/${e.credentials.id}`)} className="mt-1 flex items-center gap-2 text-left hover:underline">
                  <UserAvatar name={e.fullName} src={e.credentials.avatarThumbUrl} seed={e.credentials.id} size="sm" />
                  <span className="text-sm text-app-primary break-all">{e.credentials.email}</span>
                </button>
              ) : (
                <p className="mt-0.5 text-sm text-app-primary">—</p>
              )}
            </div>
            <Field label="Address"         value={e.address} />
          </div>
        </div>
      )}

      {activeTab === "Documents" && <HrEmployeeDocuments employeeId={id} />}

      {notification && <AlertNotification text={notification.text} status={notification.status} onClose={() => setNotification(null)} />}
    </div>
  );
}

export default HrEmployeeDetail;
